import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Plus, Search, Trash2, Users, NotebookPen, Settings } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

const PRESET_COLORS = ['#818cf8','#f87171','#4ade80','#fb923c','#22d3ee','#facc15','#e879f9','#f472b6','#a78bfa','#34d399'];
const COLORS = ['#818cf8','#f87171','#4ade80','#fb923c','#22d3ee','#facc15','#e879f9','#f472b6'];

// 아바타 컴포넌트
function Avatar({ admin, size = 'sm' }) {
  const sizeClass = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-8 h-8 text-sm';
  const color = COLORS[(admin.id || 0) % COLORS.length];
  if (admin.avatarUrl) {
    return (
      <img src={admin.avatarUrl} alt={admin.name}
        className={`${sizeClass} rounded-full object-cover border-2 border-gray-950 flex-shrink-0`} />
    );
  }
  return (
    <div className={`${sizeClass} rounded-full flex items-center justify-center font-bold border-2 border-gray-950 flex-shrink-0`}
      style={{ backgroundColor: color, color: '#fff' }}>
      {admin.name?.charAt(0)}
    </div>
  );
}

// 참가자 스택
function ParticipantStack({ participants, max = 4 }) {
  if (!participants?.length) return null;
  const admins = participants.map(p => p.admin).filter(Boolean);
  const shown = admins.slice(0, max);
  const rest = admins.length - max;
  return (
    <div className="flex items-center">
      <div className="flex -space-x-2">
        {shown.map(a => <Avatar key={a.id} admin={a} />)}
      </div>
      {rest > 0 && (
        <span className="ml-1.5 text-xs text-gray-500">+{rest}</span>
      )}
    </div>
  );
}

// 카테고리 관리 모달
function CategoryModal({ categories, onClose, onRefresh }) {
  const [form, setForm] = useState({ name: '', color: '#818cf8' });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!form.name.trim()) return;
    if (editing) await api.put(`/meeting-notes/categories/${editing.id}`, form);
    else await api.post('/meeting-notes/categories', form);
    setEditing(null);
    setForm({ name: '', color: '#818cf8' });
    onRefresh();
  };

  const remove = async (id) => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/meeting-notes/categories/${id}`);
    onRefresh();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-sm">카테고리 관리</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
        </div>
        <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
          {categories.map(c => (
            <div key={c.id} className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
              <span className="text-white text-sm flex-1">{c.name}</span>
              <button onClick={() => { setEditing(c); setForm({ name: c.name, color: c.color }); }}
                className="text-gray-500 hover:text-white p-1 text-xs">수정</button>
              <button onClick={() => remove(c.id)} className="text-gray-500 hover:text-red-400 p-1"><Trash2 size={12} /></button>
            </div>
          ))}
          {categories.length === 0 && <p className="text-gray-600 text-xs text-center py-2">등록된 카테고리가 없습니다.</p>}
        </div>
        <div className="border-t border-gray-800 pt-3">
          <p className="text-gray-400 text-xs mb-2">{editing ? '카테고리 수정' : '카테고리 추가'}</p>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="카테고리 이름" onKeyDown={e => e.key === 'Enter' && save()}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand mb-2" />
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            {PRESET_COLORS.map(c => (
              <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                className={`w-5 h-5 rounded-full border-2 transition-transform ${form.color === c ? 'border-white scale-125' : 'border-transparent hover:scale-110'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex gap-2">
            {editing && (
              <button onClick={() => { setEditing(null); setForm({ name: '', color: '#818cf8' }); }}
                className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
            )}
            <button onClick={save} className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
              {editing ? '수정' : '추가'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 회의록 작성 모달
function CreateModal({ categories, onClose, onCreate, creating }) {
  const [title, setTitle] = useState('새 회의록');
  const [categoryId, setCategoryId] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [selected, setSelected] = useState([]);
  const user = JSON.parse(localStorage.getItem('paper_work_user') || '{}');

  useEffect(() => {
    api.get('/meeting-notes/admins-list').then(r => {
      setAdmins(r.data || []);
      setSelected([user.id]);
    });
  }, []);

  const toggle = (id) => {
    if (id === user.id) return;
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h2 className="text-white font-bold text-sm mb-4">회의록 작성</h2>
        <div className="mb-3">
          <label className="text-gray-400 text-xs mb-1 block">회의 제목</label>
          <input value={title} onChange={e => setTitle(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
        </div>
        {categories.length > 0 && (
          <div className="mb-3">
            <label className="text-gray-400 text-xs mb-1 block">카테고리 <span className="text-brand">*</span></label>
            <div className="flex flex-wrap gap-1.5">
              {categories.map(c => (
                <button key={c.id} onClick={() => setCategoryId(c.id)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors flex items-center gap-1 ${
                    categoryId === c.id ? 'border-opacity-80 text-white' : 'border-gray-700 text-gray-500 hover:text-gray-300'
                  }`}
                  style={categoryId === c.id ? { backgroundColor: c.color + '33', borderColor: c.color, color: c.color } : {}}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.color }} />
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="text-gray-400 text-xs mb-2 block flex items-center gap-1"><Users size={12} /> 참가자</label>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {admins.map(a => {
              const isMe = a.id === user.id;
              const isChecked = selected.includes(a.id);
              const color = COLORS[(a.id || 0) % COLORS.length];
              return (
                <div key={a.id}
                  onClick={() => toggle(a.id)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    isChecked ? 'bg-brand/15 border border-brand/30' : 'bg-gray-800 border border-transparent hover:border-gray-700'
                  } ${isMe ? 'opacity-80' : ''}`}>
                  {a.avatarUrl
                    ? <img src={a.avatarUrl} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                    : <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: color, color: '#fff' }}>{a.name?.charAt(0)}</div>
                  }
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm">{a.name}</div>
                    <div className="text-gray-500 text-xs">{a.username}</div>
                  </div>
                  {isMe && <span className="text-gray-500 text-xs">나</span>}
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    isChecked ? 'bg-brand border-brand' : 'border-gray-600'
                  }`}>
                    {isChecked && <span className="text-white text-[10px]">✓</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex gap-2 mt-4 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
          <button onClick={() => onCreate(title, selected, categoryId)}
            disabled={creating || (categories.length > 0 && !categoryId)}
            className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80 disabled:opacity-50 disabled:cursor-not-allowed">
            {creating ? '생성 중...' : '작성 시작'}
          </button>
        </div>
      </div>
    </div>
  );
}

// 메인
export default function MeetingNotePage() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
  const isAdmin = user.role === 'superadmin' || user.role === 'admin';

  const [categories, setCategories] = useState([]);
  const [selectedCatId, setSelectedCatId] = useState('all');
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showCatModal, setShowCatModal] = useState(false);

  const LIMIT = 30;

  const loadCategories = useCallback(() =>
    api.get('/meeting-notes/categories').then(r => setCategories(r.data)), []);

  const loadNotes = useCallback(() => {
    const params = { page, limit: LIMIT };
    if (selectedCatId !== 'all') params.categoryId = selectedCatId;
    if (search.trim()) params.search = search.trim();
    api.get('/meeting-notes', { params }).then(r => {
      setNotes(r.data.notes || []);
      setTotal(r.data.total || 0);
    });
  }, [page, search, selectedCatId]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadNotes(); }, [loadNotes]);

  const doSearch = () => { setPage(1); setSearch(searchInput); };
  const selectCat = (id) => { setSelectedCatId(id); setPage(1); setSearch(''); setSearchInput(''); };

  const [creating, setCreating] = useState(false);

  const handleCreate = async (title, participantIds, categoryId) => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await api.post('/meeting-notes', {
        title: title.trim() || '새 회의록',
        participantIds,
        categoryId,
      });
      setShowCreate(false);
      navigate(`/work/meeting/${r.data.id}`);
    } catch (e) {
      alert(e.response?.data?.message || e.message || '생성 실패');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/meeting-notes/${id}`);
    loadNotes();
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 헤더 */}
      <PageHeader icon={NotebookPen} title="회의록" count={total} />

      {/* 카테고리 탭 */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-end gap-0 overflow-x-auto scrollbar-none">
          <button onClick={() => selectCat('all')}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${
              selectedCatId === 'all' ? 'text-brand border-brand' : 'text-gray-400 border-transparent hover:text-white hover:border-gray-600'
            }`}>
            전체
          </button>
          {categories.map(c => (
            <button key={c.id} onClick={() => selectCat(String(c.id))}
              className="px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 flex items-center gap-1.5"
              style={{
                color: selectedCatId === String(c.id) ? c.color : undefined,
                borderBottomColor: selectedCatId === String(c.id) ? c.color : 'transparent',
              }}>
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
              {c.name}
            </button>
          ))}
        </div>
        {isAdmin && (
          <button onClick={() => setShowCatModal(true)}
            className="pb-1 p-1.5 text-gray-600 hover:text-white rounded hover:bg-gray-800 flex-shrink-0" title="카테고리 관리">
            <Settings size={14} />
          </button>
        )}
      </div>

      <div className="border-b border-gray-800 mb-4 flex-shrink-0" />

      {/* 검색 + 작성 */}
      <div className="flex gap-2 mb-4 flex-shrink-0">
        <div className="flex-1 flex items-center bg-gray-900 border border-gray-800 rounded-lg px-3 min-w-0">
          <Search size={14} className="text-gray-500 flex-shrink-0" />
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="회의록 검색..."
            className="flex-1 bg-transparent text-white text-sm py-2 px-2 focus:outline-none min-w-0" />
          <button onClick={doSearch} className="flex-shrink-0 px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-md hover:bg-gray-600">검색</button>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 bg-brand text-white text-sm px-4 py-2 rounded-lg hover:bg-brand/80 flex-shrink-0">
          <Plus size={16} /> 작성
        </button>
      </div>

      {/* 목록 */}
      <div className="flex-1 overflow-y-auto">
        {notes.length === 0 ? (
          <div className="text-center py-16 text-gray-600 text-sm">
            {search ? '검색 결과가 없습니다.' : '회의록이 없습니다.'}
          </div>
        ) : (
          <div className="space-y-1.5">
            {notes.map(note => {
              const canDelete = note.createdBy === user.id || isAdmin;
              return (
                <div key={note.id}
                  className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 hover:border-gray-700 cursor-pointer transition-colors group"
                  onClick={() => navigate(`/work/meeting/${note.id}`)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      {note.category && (
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: note.category.color }} />
                      )}
                      <p className="text-white text-sm font-medium truncate">{note.title}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600 mt-0.5">
                      {note.category && (
                        <span style={{ color: note.category.color + 'cc' }}>{note.category.name}</span>
                      )}
                      <span>{dayjs(note.createdAt).format('YYYY.MM.DD HH:mm')}</span>
                    </div>
                  </div>
                  <ParticipantStack participants={note.participants} />
                  {canDelete && (
                    <button onClick={e => handleDelete(e, note.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-500 hover:text-red-400 flex-shrink-0 ml-1">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6 pb-4">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => Math.abs(p - page) <= 2 || p === 1 || p === totalPages)
              .map((p, i, arr) => (
                <span key={p}>
                  {i > 0 && arr[i - 1] !== p - 1 && <span className="text-gray-600 px-1">…</span>}
                  <button onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-sm ${p === page ? 'bg-brand text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
                    {p}
                  </button>
                </span>
              ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateModal
          categories={categories}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
          creating={creating}
        />
      )}

      {showCatModal && (
        <CategoryModal
          categories={categories}
          onClose={() => setShowCatModal(false)}
          onRefresh={loadCategories}
        />
      )}
    </div>
  );
}
